import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageBanner from "../components/PageBanner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { 
  Baby, Wifi, Car, Droplets, Coffee, Tv, TreePine, Zap, Wind, 
  Bath, Refrigerator, Flame, Cross, Camera 
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Baby, Wifi, Car, Droplets, Coffee, Tv, TreePine, Zap, Wind, 
  Bath, Refrigerator, Flame, Cross, Camera
};

export default function FacilitiesFaqs() {
  const [amenities, setAmenities] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [amenitiesRes, faqsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/amenities`),
          axios.get(`${API}/faqs`),
          axios.get(`${API}/settings`)
        ]);
        setAmenities(amenitiesRes.data);
        setFaqs(faqsRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="facilities-faqs-page">
      <Header />
      
      <PageBanner
        title="FACILITIES AND FAQ'S"
        breadcrumbs={[{ name: "FACILITIES AND FAQS" }]}
        backgroundImage="/assets/faq_banner.png"
      />

      {/* Where Serenity Meets Convenience */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-6">
            WHERE SERENITY MEETS CONVENIENCE
          </h2>
          <p className="text-[#7F8C8D] leading-relaxed">
            {settings?.about_text || "Escape the chaos of urban life to WyldWoods, a tranquil village stay nestled amidst nature's splendor, yet conveniently located near the city making it an ideal getaway for those seeking relaxation and connection with the natural world. WyldWoods offers you Cozy, rustic-chic accommodations with modern amenities, Easy access to nearby cities, attractions, fun filled activities and Warm hospitality, tailored to your needs."}
          </p>
          <Link
            to="/bookings"
            className="inline-block mt-8 btn-secondary"
            data-testid="check-availability-btn"
          >
            Check Availability
          </Link>
        </div>
      </section>

      {/* Facilities Section - Enhanced */}
      <section className="py-24 px-4 bg-white relative overflow-hidden" data-testid="facilities-section">
        <div className="absolute top-0 left-0 w-full h-full opacity-5">
          <div className="absolute top-20 right-20 w-72 h-72 bg-[#E67E22] rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-72 h-72 bg-[#F39C12] rounded-full filter blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#FEF3E2] to-[#FFE5CC] rounded-full mb-4">
              <div className="w-2 h-2 bg-[#E67E22] rounded-full animate-pulse"></div>
              <span className="text-[#E67E22] text-sm font-bold tracking-widest">WHAT WE OFFER</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#2C3E50] mb-4">
              <span className="gradient-text">Facilities</span>
            </h2>
            <p className="text-[#7F8C8D] text-lg max-w-2xl mx-auto">Everything you need for a perfect stay at WyldWoods</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-8">
            {amenities.map((amenity, index) => {
              const IconComponent = iconMap[amenity.icon] || Wifi;
              return (
                <div 
                  key={amenity.id || index} 
                  className="text-center group animate-scale-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`facility-${index}`}
                >
                  <div className="amenity-icon mx-auto">
                    <IconComponent size={32} className="group-hover:animate-bounce" />
                  </div>
                  <p className="text-sm text-[#2C3E50] font-semibold tracking-wide group-hover:text-[#E67E22] transition-colors">
                    {amenity.name}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section - Enhanced */}
      <section className="py-24 px-4 bg-gradient-to-br from-white via-[#FDFBF7] to-[#FFF8F0] relative overflow-hidden" data-testid="faq-section">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#E67E22]/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-[#F39C12]/10 to-transparent rounded-full blur-3xl"></div>
        
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#FEF3E2] to-[#FFE5CC] rounded-full mb-4">
              <div className="w-2 h-2 bg-[#E67E22] rounded-full animate-pulse"></div>
              <span className="text-[#E67E22] text-sm font-bold tracking-widest">GOT QUESTIONS?</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#2C3E50] mb-4">
              Frequently Asked <span className="gradient-text">Questions</span>
            </h2>
            <p className="text-[#7F8C8D] text-lg max-w-2xl mx-auto">Find quick answers to common questions about your stay</p>
          </div>

          <div className="premium-card p-8 shadow-2xl animate-fade-in-up">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={faq.id || index} 
                  value={`faq-${index}`}
                  className="border-none bg-gradient-to-br from-[#FDFBF7] to-white rounded-xl px-6 py-2 transition-all duration-300 hover:shadow-lg hover:scale-[1.02]"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger 
                    className="text-left text-[#2C3E50] font-semibold hover:text-[#E67E22] py-5 [&[data-state=open]]:text-[#E67E22] group no-underline hover:no-underline [&>svg]:text-[#E67E22]"
                    data-testid={`faq-question-${index}`}
                  >
                    <div className="flex items-center space-x-4 w-full">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E67E22] to-[#F39C12] flex items-center justify-center flex-shrink-0 text-white font-bold text-base group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-md">
                        {String(index + 1).padStart(2, '0')}
                      </div>
                      <span className="flex-1 font-serif text-xl md:text-2xl font-bold tracking-tight">{faq.question}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-[#7F8C8D] leading-relaxed pl-16 pr-4 pb-5">
                    <div className="flex items-start space-x-4">
                      <div className="w-1 h-full bg-gradient-to-b from-[#E67E22] to-[#F39C12] rounded-full"></div>
                      <p className="text-lg md:text-xl font-sans">{faq.answer}</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {/* Contact CTA */}
            <div className="mt-10 p-6 bg-gradient-to-r from-[#E67E22] to-[#F39C12] rounded-2xl text-white text-center">
              <h3 className="font-serif text-2xl font-bold mb-2">Still have questions?</h3>
              <p className="mb-4 text-white/90">Our team is here to help you 24/7</p>
              <Link
                to="/contact"
                className="inline-block bg-white text-[#E67E22] font-bold py-3 px-8 rounded-lg hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
